import * as React from "react";
import moment from "moment";

import { Key, EntityType, Sensor, SensorDraft, GeotabSensor } from "../../../../models";

import { Container } from "../../../Container";
import { addNewRouteFor, editRouteFor, sensorsDataRoute, appNavigator } from "../../../routes";

import { EditCreateEntityRoute, EditCreateEntityProps } from "../EditCreate/EditCreateEntityRoute";
import { EditSensorActions, EditSensorStore } from "./flux";

import { NewSensor } from "./NewSensor";
import { SensorGrid } from "./SensorGrid";
import { Button } from "../../../../components/Buttons";
import { DateRange } from "../../../../components/DateRange";
import { DateRangeStore, DateRangeActions } from "./DateRangeStore";
import { OneButtonDialog } from "../../Dialogs/OneButtonDialog";
import { EditCreateEntityActions } from "../../../actions/EditCreateEntityActions";
import { FlagStore } from "../../../flux/Flag/FlagStore";
import { ErrorStore } from "../../../flux/Error/ErrorStore";
import { ErrorActions } from "../../../flux/Error/ErrorActions";
import { ListStore } from "../../../flux/Grid/ListStore";
import { SelectedItemsStore } from "../../../flux/Grid/SelectedItemsStore";
import { SelectedItemsActions } from "../../../flux/Grid/SelectedItemsActions";
import { twoButtonDialogConfig } from "../../../utils/configure-store";
import { Toolbar } from "../../Toolbar";
import { SearchProps } from "../SearchProps";
import { NoResults } from "../../Table/NoResults";
import { TableTitle } from "../../Table/TableTitle";
import { LabelStore } from "../../../flux/Label/LabelStore";
import { Spinner } from "../../../../components/Spinner";
import { ErrorMessage } from "../../../../components/ErrorMessage";
import { Polyglot } from "../../../../polyglot";
import { StickyMediator } from "../../../utils/StickyMediator";
import { FlagActions } from "../../../flux/Flag/FlagActions";
import { ValueStore } from "../../../flux/Value/ValueStore";
import { GenericSearchWidget } from "../Search/GenericSearchWidget";
import { TrashButtonWidget } from "../../TrashButtonWidget";
import { DeleteConfirmationWidget, DeleteItemsProps } from "../../DeleteConfirmationWidget";
import { Command1, Command2 } from "../../../../Command";
import renderHTML from 'react-render-html';

const appStyles = require('../../../app.scss');
const tableStyles = require('../../../../components/Table.scss');

interface TabProps
{
    polyglot: Polyglot;
    isAdmin: boolean;

    editStore: EditSensorStore;

    loadingVehicles: FlagStore;
    loadingGeotabSensors: FlagStore;
    loadingQuantities: FlagStore;
    upsertInProgressStore: FlagStore;
    upsertErrorStore: ErrorStore;
    upsertCommand: Command1<SensorDraft>;

    toolbarErrorStore: ErrorStore;
    alertVisibilityStore: FlagStore;
    loadSensorsDataInProgressStore: FlagStore;
    loadSensorsDataCmd: Command1<string>;

    tableTitleStore: LabelStore;
    listStore: ListStore<Sensor>;
    selectedItemsStore: SelectedItemsStore<Sensor>;

    dateRangeStore: DateRangeStore;

    vehicleSearchInitiatorStore: ValueStore<Key>;
    vehicleSearchCmd: Command2<Key, any>;

    upsertSetVehicle: Command1<Key>;
    upsertSetGeotabSensor: Command1<GeotabSensor>;
    upsertInitCmd: Command1<Key>;
}

type Props = TabProps & SearchProps & DeleteItemsProps;
export class SensorTab extends React.Component<Props>
{
    private readonly errorActions = new ErrorActions();
    private readonly dateRangeActions = new DateRangeActions();
    private readonly editCreateEntityActions = new EditCreateEntityActions();
    private readonly selectedItemsActions = new SelectedItemsActions();
    private readonly stickyMediator = new StickyMediator();

    state = {
        allowDeletionOfSensors: JSON.parse(localStorage.getItem("allowDeletionOfSensors"))
    }

    componentDidMount()
    {
        // this.props.refresher.run();
    }

    render()
    {
        const {
            dateRangeStore,
            selectedItemsStore,
            isAdmin,
            polyglot,
        } = this.props

        const entityType = EntityType.Sensor;
        const createForm = ({ title, okTitle, path }) =>(
            <Container merge stores={twoButtonDialogConfig(this.props.upsertInProgressStore, this.props.upsertErrorStore)}>
                <EditCreateEntityRoute title={title} okTitle={okTitle} path={path}
                    cancelTitle={polyglot.t('button.cancel')}
                    renderEdit={() => (
                        <Container
                            merge stores={[
                                this.props.editStore,
                                [this.props.loadingVehicles, (store: FlagStore) => ({ loadingVehicles: store.isSet() })],
                                [this.props.loadingGeotabSensors, (store: FlagStore) => ({ loadingGeotabSensors: store.isSet() })],
                                [this.props.loadingQuantities, (store: FlagStore) => ({ loadingQuantities: store.isSet() })]
                            ]}
                            actions={new EditSensorActions()}
                            onMount={() => {
                                const { VehicleId } = this.props.editStore.getState().draft;
                                this.props.upsertInitCmd.execute(VehicleId);
                            }}>
                                <NewSensor polyglot={polyglot} loadingVehicles={null} loadingGeotabSensors={null} loadingQuantities={null}
                                    setVehicleCmd={this.props.upsertSetVehicle}
                                    setGeotabSensorCmd={this.props.upsertSetGeotabSensor}
                                />
                        </Container>
                    )}
                    cancel={() => appNavigator.goToEntityTab(entityType)}
                    ok={() => this.props.upsertCommand.execute(this.props.editStore.getState().draft)}
                />
            </Container>
        );

        const disableInject = {
            disabled: () => selectedItemsStore.getState().selected.size === 0
        };

        return (
            <div>
                <OneButtonDialog
                    visibilityStore={this.props.alertVisibilityStore}
                    title={polyglot.t('misc.info')}
                    okTitle={polyglot.t('button.ok')}
                    ok={() => (new FlagActions()).reset(this.props.alertVisibilityStore.dispatchToken)}>
                        <div>{polyglot.t('sensor.no_data_msg')}</div>
                </OneButtonDialog>
                {isAdmin && createForm({title: polyglot.t('sensor.create_title'), okTitle: polyglot.t('button.create'), path: addNewRouteFor(entityType)})}
                {isAdmin && createForm({title: polyglot.t('sensor.edit_title'), okTitle: polyglot.t('button.save'), path: editRouteFor(entityType)})}
                <DeleteConfirmationWidget {...this.props} entityType={entityType} title={polyglot.t('sensor.del_confirmation_title')}>
                    <span className={appStyles.ConfirmationContainer}>{renderHTML(polyglot.t('sensor.del_confirmation_msg'))}</span>
                </DeleteConfirmationWidget>
                <GenericSearchWidget {...this.props} />
                <div className={appStyles.StickyControlPanel} ref={this.stickyMediator.onToolbox}>
                    <Toolbar>
                        { isAdmin &&
                            <Button type="success" leftIcon="plus" onClick={() => {
                                this.editCreateEntityActions.rejectDraft(entityType);
                                this.errorActions.clearError(this.props.upsertErrorStore.dispatchToken);
                                appNavigator.goToAddNewEntityTab(entityType);
                            }}>{this.props.polyglot.t('button.new')}</Button>
                        }
                        <Container inject={disableInject} stores={[selectedItemsStore]}>
                            <Button onClick={() => this.props.loadSensorsDataCmd.execute('last24h')}>{polyglot.t('sensor.btn_last_24h')}</Button>
                        </Container>
                        <Container inject={disableInject} stores={[selectedItemsStore]}>
                            <Button onClick={() => this.props.loadSensorsDataCmd.execute('yesterday')}>{polyglot.t('sensor.btn_yesterday')}</Button>
                        </Container>
                        <Container stores={{
                            disabled: () => ({ value: selectedItemsStore.getState().selected.size === 0, store: selectedItemsStore }),
                            fromDate: () => ({ value: dateRangeStore.getState().fromDate, store: dateRangeStore }),
                            toDate: () => ({ value: dateRangeStore.getState().toDate, store: dateRangeStore }),
                        }}>
                            <DateRange
                                locale={polyglot.locale()}
                                fromTitle={polyglot.t('sensor.placeholder_from')}
                                toTitle={polyglot.t('sensor.placeholder_to')}
                                buttonTitle={polyglot.t('sensor.btn_go')}
                                disabled={null} fromDate={null} toDate={null}
                                setFromDate={date => this.dateRangeActions.setFromDate(date)}
                                setToDate={date => this.dateRangeActions.setToDate(date)}
                                onClick={e => this.props.loadSensorsDataCmd.execute(this.getDateRange())}
                            />
                        </Container>
                        <Container subscribe={[this.props.loadSensorsDataInProgressStore]} render={() => this.props.loadSensorsDataInProgressStore.isSet() && <Spinner />} />
                        <Container store={this.props.toolbarErrorStore}>
                            <ErrorMessage />
                        </Container>
                        { (isAdmin && this.state.allowDeletionOfSensors) && <TrashButtonWidget entityType={EntityType.Sensor} {...this.props} /> }
                    </Toolbar>
                    <TableTitle polyglot={polyglot} labelStore={this.props.tableTitleStore} />
                </div>    
                <Container merge stores={[
                    this.props.listStore, selectedItemsStore,
                    [this.props.vehicleSearchInitiatorStore, (store: ValueStore<Key>) => ({ vehicleSearchInitiator: store.getValue() })]
                ]}>
                    <SensorGrid
                        className={tableStyles.StickyTable}
                        polyglot={polyglot}
                        isAdmin={isAdmin}
                        vehicleSearchInitiator={null}
                        items={null} selected={null} allSelected={null}
                        onClone={(datum, row) => {
                            this.editCreateEntityActions.setEntityPrototype(datum, entityType);
                            this.errorActions.clearError(this.props.upsertErrorStore.dispatchToken);
                            appNavigator.goToAddNewEntityTab(entityType);
                        }}
                        onEdit={(datum, row) => {
                            this.editCreateEntityActions.setEntityToEdit(datum, entityType);
                            this.errorActions.clearError(this.props.upsertErrorStore.dispatchToken);
                            appNavigator.goToEditEntityTab(entityType);
                        }}
                        onVehicle={(datum: Sensor, row) => this.props.vehicleSearchCmd.execute(datum.id, { SensorId: datum.id })}
                        toggle={(datum: Sensor, row) => this.selectedItemsActions.toggleItem(datum.id, entityType)}
                        toggleAll={() => this.selectedItemsActions.toggleAll(entityType)}
                        onThead={this.stickyMediator.onThead}
                    />
                </Container>
                <NoResults polyglot={polyglot} listStore={this.props.listStore} />
            </div>
        );
    }

    private getDateRange()
    {
        const format = 'YYYY-MM-DD';
        const { fromDate, toDate } = this.props.dateRangeStore.getState();
        (window as any).fromDate = fromDate;
        (window as any).toDate = toDate;
        return (fromDate && toDate)
            ? `${fromDate.format(format)} - ${toDate.format(format)}`
            : (fromDate || toDate).format(format);
    }
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Sensor/SensorRoute.tsx